import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export type WorkspaceRole = "owner" | "admin" | "editor" | "viewer";

export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: WorkspaceRole;
  status: string;
}

interface WorkspaceWithRole extends Workspace {
  role: WorkspaceRole;
}

interface WorkspaceContextType {
  activeWorkspaceId: string | null;
  activeWorkspace: WorkspaceWithRole | null;
  workspaces: WorkspaceWithRole[];
  currentRole: WorkspaceRole | null;
  isLoading: boolean;
  isSwitching: boolean;
  switchingToName: string | null;
  switchWorkspace: (workspaceId: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const STORAGE_KEY = "subtrackr_active_workspace";

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });

  // Fetch all workspaces the user belongs to
  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ["workspaces", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: members, error } = await supabase
        .from("workspace_members")
        .select("workspace_id, role, workspaces:workspace_id(id, name, owner_id, created_at, updated_at)")
        .eq("user_id", user.id)
        .eq("status", "active");

      if (error) {
        console.error("Error fetching workspaces:", error);
        return [];
      }

      return (members || [])
        .filter((m: any) => m.workspaces)
        .map((m: any) => ({
          ...m.workspaces,
          role: m.role as WorkspaceRole,
        })) as WorkspaceWithRole[];
    },
    enabled: !!user?.id,
  });

  // Auto-select workspace if none is active or current one is invalid
  useEffect(() => {
    if (workspaces.length === 0) return;

    const currentIsValid = workspaces.some((w) => w.id === activeWorkspaceId);
    if (!activeWorkspaceId || !currentIsValid) {
      // Prefer the owned workspace, otherwise first
      const owned = workspaces.find((w) => w.role === "owner");
      const fallback = owned || workspaces[0];
      setActiveWorkspaceId(fallback.id);
      localStorage.setItem(STORAGE_KEY, fallback.id);
    }
  }, [workspaces, activeWorkspaceId]);

  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingToName, setSwitchingToName] = useState<string | null>(null);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (workspaceId === activeWorkspaceId) return;
      const target = workspaces.find((w) => w.id === workspaceId);
      setSwitchingToName(target?.name || null);
      setIsSwitching(true);
      setActiveWorkspaceId(workspaceId);
      localStorage.setItem(STORAGE_KEY, workspaceId);
      // Remove all cached workspace-scoped data immediately; components
      // will refetch their own queries on next render. Don't await any
      // refetches — that's what made switching feel slow.
      queryClient.removeQueries({
        predicate: (q) => q.queryKey[0] !== "workspaces",
      });
      // Yield once so React can paint the new workspace state, then close overlay.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      setIsSwitching(false);
      setSwitchingToName(null);
    },
    [queryClient, workspaces, activeWorkspaceId]
  );


  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || null;
  const currentRole = activeWorkspace?.role || null;

  return (
    <WorkspaceContext.Provider
      value={{
        activeWorkspaceId,
        activeWorkspace,
        workspaces,
        currentRole,
        isLoading,
        isSwitching,
        switchingToName,
        switchWorkspace,
      }}
    >
      {/* Keying on activeWorkspaceId forces a remount of the subtree on
          workspace change. This guarantees every hook that reads the
          workspace id from localStorage (via getActiveWorkspaceId) re-runs
          its queries against the newly selected workspace without requiring
          a manual page refresh. */}
      <div key={activeWorkspaceId ?? "no-workspace"} style={{ display: "contents" }}>
        {children}
      </div>
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
